<template>
    <b-container fluid v-if="customer_behavior_context_loaded">
      <div class="white-box mb-4 p-4">
        <div class="chart-header">
          <h5 class="text-base mb-2">{{ chartTitle }}</h5>
          <b-button
            v-if="showCheckoutDetails"
            variant="link"
            class="p-0 step-action"
            @click="toggleCheckoutDetails(false)"
          >
            Voltar
          </b-button>
        </div>

        <div class="live-funnel mt-4">
          <div
            class="flow-chart-container"
            v-for="(step, index) in displaySteps"
            :key="step.label"
          >
            <div class="step-info">
              <span class="step-title">{{ step.label }}</span>
              <div v-if="!showCheckoutDetails && step.isCheckout && canShowCheckoutDetails" class="d-flex align-items-center gap-2">
                <span class="flow-num">{{ formatNumber(parseNumber(step.displayValue)) }}</span>

                <b-button
                  variant="link"
                  class="p-0 step-action"
                  @click="toggleCheckoutDetails(true)"
                >
                  Ver mais
                </b-button>
              </div>
              <span v-else class="flow-num">{{ formatNumber(parseNumber(step.displayValue)) }}</span>
            </div>

            <div class="flow-chart">
                <div
                  class="flow-chart-box"
                  :style="boxStyle(step.height, index === displaySteps.length - 1)"
                ></div>
                <div
                  class="flow-chart-box-connector"
                  v-if="index < displaySteps.length - 1 && (step.height > 0 || (displaySteps[index + 1] && displaySteps[index + 1].height > 0))"
                  :style="connectorStyle(step.height, displaySteps[index + 1] ? displaySteps[index + 1].height : 0)"
                ></div>
            </div>
          </div>
        </div>
      </div>
    </b-container>
    <loader-box v-else height="400px" class="mb-4 mr-2 ml-2"/>
  </template>
  
<script>
  import LoaderBox from '@/components/LoaderBox.vue'
  
  export default {
    components: { LoaderBox },
    data() {
      return {
        vbW: 1000,        // largura do viewBox do SVG
        vbH: 160,         // altura do viewBox do SVG
        pad: 24,          // padding lateral
        slopeW: 90,       // largura das diagonais de descida
        gradId: 'gf-grad',
        shineId: 'gf-shine',
        showCheckoutDetails: false
      }
    },
    computed: {
      customer_behavior_context_loaded() { return this.$store.getters.customer_behavior_context_loaded },
      totalVisits()   { return this.$store.getters.totalVisits   || 0 },
      totalCarts()    { return this.$store.getters.totalCarts    || 0 },
      totalCheckout() { return this.$store.getters.totalCheckout || 0 },
      funilCheckout() { return this.$store.getters.funilCheckout || 0 },
      funilPedidos()  { return this.$store.getters.funilPedidos  || 0 },
      totalConverted() { return this.$store.getters.totalConverted || 0 },
      funilLoginValue() { return this.parseNumber(this.$store.getters.funilLogin || 0) },
      funilFreteValue() { return this.parseNumber(this.$store.getters.funilFrete || 0) },
      funilPagamentoValue() { return this.parseNumber(this.$store.getters.funilPagamento || 0) },

      chartTitle() {
        return this.showCheckoutDetails ? 'Detalhes do checkout' : 'Comportamento do cliente'
      },

      maxFunnelValue() {
        return Math.max(
          this.parseNumber(this.totalVisits),
          this.parseNumber(this.totalCarts),
          this.parseNumber(this.funilCheckout),
          this.parseNumber(this.funilPedidos),
          1
        )
      },

      h1(){ return this.scaleVal(this.parseNumber(this.totalCarts)) },
      h2(){ return this.scaleVal(this.parseNumber(this.funilCheckout)) },
      h3(){ return this.scaleVal(this.parseNumber(this.funilPedidos)) },
      
      // Alturas em pixels para os boxes (com um mínimo e máximo)
      // Retorna 0 quando não houver dados
      boxHeightVisits(){ return this.calculateBoxHeight(this.totalVisits) },
      boxHeightCarts(){ return this.calculateBoxHeight(this.totalCarts) },
      boxHeightCheckout(){ return this.calculateBoxHeight(this.funilCheckout) },
      boxHeightConverted(){ return this.calculateBoxHeight(this.funilPedidos) },
      checkoutDetailMaxValue() {
        return Math.max(
          this.funilLoginValue,
          this.funilFreteValue,
          this.funilPagamentoValue,
          1
        )
      },
      checkoutLoginHeight() { return this.calculateDetailBoxHeight(this.funilLoginValue) },
      checkoutFreteHeight() { return this.calculateDetailBoxHeight(this.funilFreteValue) },
      checkoutPagamentoHeight() { return this.calculateDetailBoxHeight(this.funilPagamentoValue) },

      flowSteps() {
        return [
          {
            label: 'Visitantes',
            displayValue: this.totalVisits,
            height: this.boxHeightVisits,
            isCheckout: false
          },
          {
            label: 'Carrinhos ativos',
            displayValue: this.totalCarts,
            height: this.boxHeightCarts,
            isCheckout: false
          },
          {
            label: 'Em checkout',
            displayValue: this.totalCheckout,
            height: this.boxHeightCheckout,
            isCheckout: true
          },
          {
            label: 'Compras',
            displayValue: this.totalConverted,
            height: this.boxHeightConverted,
            isCheckout: false
          }
        ]
      },

      checkoutDetailSteps() {
        return [
          {
            label: 'Login',
            displayValue: this.funilLoginValue,
            height: this.checkoutLoginHeight,
            isCheckout: false
          },
          {
            label: 'Frete',
            displayValue: this.funilFreteValue,
            height: this.checkoutFreteHeight,
            isCheckout: false
          },
          {
            label: 'Pagamento',
            displayValue: this.funilPagamentoValue,
            height: this.checkoutPagamentoHeight,
            isCheckout: false
          }
        ]
      },

      displaySteps() {
        return this.showCheckoutDetails ? this.checkoutDetailSteps : this.flowSteps
      },

      canShowCheckoutDetails() {
        return [this.funilLoginValue, this.funilFreteValue, this.funilPagamentoValue]
          .some(value => value > 0)
      },
  
      // path do shape principal (degraus horizontais com descidas diagonais)
      areaPath(){
        const W = this.vbW, H = this.vbH, P = this.pad, S = this.slopeW
        const usable = W - P*2
        const colW = usable / 3
  
        const x0 = P
        const x1 = x0 + colW - S*0.25          // fim do 1º patamar (antes da diagonal)
        const x1s = x1 + S                      // fim da 1ª diagonal
        const x2 = x1s + colW - S*0.5           // fim do 2º patamar
        const x2s = x2 + S                      // fim da 2ª diagonal
        const xEnd = P + usable                 // fim total
  
        const y1 = H - this.h1
        const y2 = H - this.h2
        const y3 = H - this.h3
  
        // polígono da área
        return [
          `M ${x0} ${H}`,        // base esquerda
          `L ${x0} ${y1}`,       // sobe ao topo 1
          `L ${x1} ${y1}`,       // 1º patamar
          `L ${x1s} ${y2}`,      // diagonal para topo 2
          `L ${x2} ${y2}`,       // 2º patamar
          `L ${x2s} ${y3}`,      // diagonal para topo 3
          `L ${xEnd-12} ${y3}`,  // 3º patamar (deixa 12px para canto arredondado)
          `L ${xEnd-12} ${H}`,   // desce para base
          `Z`
        ].join(' ')
      },
  
      // canto arredondado no final
      roundR(){ return 10 },
      roundX(){ return this.vbW - this.pad - this.roundR }
      ,
      roundY(){
        const H = this.vbH
        return H - this.h3 - this.roundR
      },
  
      // posições das duas linhas finas no topo do 1º patamar
      firstLines(){
        const W = this.vbW, P = this.pad, S = this.slopeW
        const usable = W - P*2
        const colW = usable / 3
        const xStart = P + colW * 0.12
        const xEnd   = P + colW * 0.58
        const yTop   = this.vbH - this.h1 + 10  // um pouco abaixo do topo
        return { x1: xStart, x2: xEnd, y1: yTop, y2: yTop + 10 }
      }
    },
    methods: {
      parseNumber(v){
        if (typeof v === 'number') return v
        if (typeof v === 'string') return parseInt(v.replace(/\./g,'')) || 0
        return 0
      },
      formatNumber(v){ return v.toLocaleString('pt-BR') },
      pct(v){ return (v/this.maxCity*100).toFixed(2) },
  
      // mapeia o valor para altura: mínimo 28, máximo 110 (em px do viewBox)
      scaleVal(v){
        const vals = [
          this.parseNumber(this.totalVisits),
          this.parseNumber(this.totalCarts),
          this.parseNumber(this.funilCheckout),
          this.parseNumber(this.funilPedidos)
        ]
        const max = Math.max(...vals, 1)
        const minH = 28, maxH = 110
        return minH + (maxH - minH) * (v / max)
      },

      calculateBoxHeight(value) {
        const val = this.parseNumber(value)
        const max = this.maxFunnelValue
        if (max === 0 || val === 0) return 0
        const minH = 28, maxH = 200
        return minH + (maxH - minH) * (val / max)
      },

      calculateDetailBoxHeight(value) {
        const val = this.parseNumber(value)
        const max = this.checkoutDetailMaxValue
        if (max === 0 || val === 0) return 0
        const minH = 28, maxH = 200
        return minH + (maxH - minH) * (val / max)
      },

      boxStyle(height, isLast) {
        const width = isLast ? '100%' : '80%'
        return height > 0 ? { width, height: `${height}px` } : { width }
      },

      connectorStyle(currentHeight, nextHeight) {
        const maxHeight = Math.max(currentHeight, nextHeight)
        if (maxHeight === 0) return {}

        const topCurrent = currentHeight > 0 && (currentHeight >= nextHeight || nextHeight === 0)
          ? '0%'
          : (currentHeight > 0 && nextHeight > 0
            ? `${((nextHeight - currentHeight) / nextHeight) * 100}%`
            : (currentHeight > 0 ? '0%' : '100%'))

        const topNext = nextHeight > 0 && (nextHeight >= currentHeight || currentHeight === 0)
          ? '0%'
          : (nextHeight > 0 && currentHeight > 0
            ? `${((currentHeight - nextHeight) / currentHeight) * 100}%`
            : (nextHeight > 0 ? '0%' : '100%'))

        return {
          '--connector-height': `${maxHeight}px`,
          '--top-current': topCurrent,
          '--bottom-current': '100%',
          '--top-next': topNext,
          '--bottom-next': '100%',
          '--connector-end-x': '16.67%',
          top: 'auto',
          bottom: '0',
          left: '80%',
          width: 'calc(20% + 100%)'
        }
      },

      toggleCheckoutDetails(show) {
        this.showCheckoutDetails = show
      }
    }
  }
</script>

<style scoped>
.chart-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px;
}

.step-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
    width: 100%;
}

.step-title {
    font-weight: 600;
}

.step-action {
    width: 100%;
    font-size: 12px;
    display: flex;
    justify-content: flex-end;
    text-decoration: underline;
}

.step-info .step-action {
    align-self: flex-start;
}

.live-funnel {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    position: relative;
    overflow: visible;
}

.flow-chart-container {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: start;
    justify-content: space-between;
    width: 100%;
    min-height: 300px;
    border-right: 1px solid #e0e0e0;
    padding-right: 10px;
    padding-left: 10px;
    overflow: hidden;
}

.flow-chart-container:first-child {
    padding-left: 0;
}

.flow-chart-container:last-child {
    padding-right: 0;
}

.flow-chart-container:last-child {
    border-right: none;
}

.flow-chart-container span {
    display: flex;
    flex-direction: column;
    width: 100%;
}

.flow-num {
    font-weight: 600;
}

.flow-chart {
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: flex-end; /* Alinha os boxes pela base */
}

.flow-chart-box {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #72AA18;
    z-index: 2;
}

.flow-chart-container:first-child .flow-chart-box {
    border-top-left-radius: 10px;
}

.flow-chart-container:last-child .flow-chart-box{
    border-top-right-radius: 10px;
}

.flow-chart-box-connector {
    position: absolute;
    background-color: #72AA18;
    opacity: 0.7;
    z-index: 1;
    pointer-events: none;
    top: auto;
    bottom: 0;
    left: 80%;
    width: calc(20% + 100%);
    height: var(--connector-height, 200px);
    clip-path: polygon(
        0% var(--top-current, 0%),
        0% var(--bottom-current, 100%),
        var(--connector-end-x, 16.67%) var(--bottom-next, 100%),
        var(--connector-end-x, 16.67%) var(--top-next, 0%)
    );
}
</style>